import { useEffect, useRef, useState } from "react";
import { BookOpen, Loader2, Send, X } from "lucide-react";
import { cn } from "./ui";
import { api, apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Помощник базы знаний (baza.starec.ai) прямо в Hub: менеджер спрашивает о цене, наличии, условиях, документах клиента —
   база отвечает по своим документам (договоры, прайсы, каталоги, реквизиты) со ссылками на источники. Из диалога вопрос
   уходит вместе с последними сообщениями переписки, ответ можно одним нажатием вставить в поле ответа клиенту. */

export type BazaSource = {
  type: string;
  id: string;
  label: string;
  url: string;
};
type Answer = { q: string; answer: string; sources: BazaSource[] };

export const bazaApi = {
  status: () => api<{ enabled: boolean; url: string }>("/baza/status"),
  ask: (body: {
    question: string;
    conversationId?: string;
    companyId?: string;
    threadId?: string | null;
  }) =>
    api<{ threadId: string | null; answer: string; sources: BazaSource[] }>(
      "/baza/ask",
      { method: "POST", json: body },
    ),
};

/** подключена ли база знаний на сервере (BAZA_TOKEN): спрашиваем один раз на вкладку */
let enabledOnce: Promise<boolean> | null = null;
export function useBazaEnabled(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    enabledOnce ??= bazaApi.status().then(
      (r) => r.enabled,
      () => false,
    );
    let alive = true;
    void enabledOnce.then((v) => alive && setOn(v));
    return () => {
      alive = false;
    };
  }, []);
  return on;
}

/** ответ без служебных строк «Источник: …» — ссылки показываются отдельно */
const clean = (s: string) =>
  s
    .split("\n")
    .filter((l) => !/^\s*(Источник|Источники|Source|Sources)\s*:/i.test(l))
    .join("\n")
    .trim();

const QUICK = [
  t("Цена и наличие товара"),
  t("Последние счета и договоры этого клиента"),
  t("Реквизиты компании для счёта"),
];

export function BazaAsk({
  conversationId,
  companyId,
  onInsert,
  onClose,
  compact,
}: {
  conversationId?: string;
  companyId?: string;
  onInsert?: (text: string) => void;
  onClose?: () => void;
  compact?: boolean;
}) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<Answer[]>([]);
  const thread = useRef<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    thread.current = null;
    setItems([]);
    setError("");
  }, [conversationId, companyId]);

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await bazaApi.ask({
        question: text,
        conversationId,
        companyId,
        threadId: thread.current,
      });
      thread.current = r.threadId;
      setItems((xs) => [
        ...xs,
        { q: text, answer: clean(r.answer), sources: r.sources },
      ]);
      setQ("");
    } catch (e) {
      setError(apiErrorText(e));
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-border bg-sky-500/5 p-3",
        compact ? "rounded-btn border" : "border-b",
      )}
      data-testid="baza-ask"
    >
      <div className="flex items-center gap-1.5">
        <BookOpen className="size-3.5 text-sky-600" aria-hidden />
        <p className="whitespace-nowrap text-[12px] font-semibold text-foreground">
          {t("Спросить базу знаний")}
        </p>
        <span className="min-w-0 truncate text-[10.5px] text-muted-foreground">
          {t("прайсы, каталоги, договоры, счета, реквизиты")}
        </span>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть")}
            className="ms-auto grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground hover:bg-muted"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {items.length === 0 && !busy ? (
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => setQ(x)}
              className="cursor-pointer rounded-full border border-border bg-background px-2.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {x}
            </button>
          ))}
        </div>
      ) : null}
      <div
        className={cn(
          "flex flex-col gap-2 overflow-y-auto",
          compact ? "max-h-[420px]" : "max-h-[260px]",
        )}
      >
        {items.map((a, i) => (
          <div
            key={i}
            className="rounded-btn border border-border bg-background p-2.5"
            data-testid="baza-answer"
          >
            <p className="text-[11px] font-semibold text-muted-foreground">
              {a.q}
            </p>
            <p className="mt-1 whitespace-pre-wrap break-words text-[12px] leading-relaxed text-foreground">
              {a.answer}
            </p>
            {a.sources.length ? (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {a.sources.slice(0, 8).map((s) => (
                  <a
                    key={`${s.type}:${s.id}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-[16rem] items-center gap-1 truncate rounded-full bg-sky-500/10 px-2 py-0.5 text-[10.5px] font-medium text-sky-700 hover:underline"
                    data-testid="baza-source"
                  >
                    <i className="ri-file-text-line text-[11px]" aria-hidden />
                    <span className="truncate">{s.label}</span>
                  </a>
                ))}
              </div>
            ) : null}
            {onInsert && a.answer ? (
              <button
                type="button"
                onClick={() => onInsert(a.answer)}
                className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                data-testid="baza-insert"
              >
                <i className="ri-reply-line" aria-hidden />
                {t("Вставить в ответ")}
              </button>
            ) : null}
          </div>
        ))}
        {busy ? (
          <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            {t("База знаний ищет ответ по документам…")}
          </p>
        ) : null}
      </div>
      {error ? <p className="text-[11.5px] text-red-500">{error}</p> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void ask(q);
        }}
        className="flex items-end gap-1.5"
      >
        <textarea
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void ask(q);
            }
          }}
          rows={1}
          autoFocus
          placeholder={
            items.length
              ? t("Уточнить вопрос…")
              : t("Например: цена стула Lissandra и срок поставки")
          }
          aria-label={t("Вопрос базе знаний")}
          className="min-h-9 flex-1 resize-none rounded-btn border border-border bg-background px-2.5 py-2 text-[12.5px] text-foreground outline-none focus:border-primary"
          data-testid="baza-question"
        />
        <button
          type="submit"
          disabled={!q.trim() || busy}
          aria-label={t("Спросить")}
          className="grid size-9 flex-none cursor-pointer place-items-center rounded-btn bg-sky-600 text-white transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
        >
          <Send className="size-3.5" aria-hidden />
        </button>
      </form>
    </div>
  );
}
